import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Loader } from '..';
import WorkflowMessagePanel from '../WorkflowMessagePanel';
import { startWorkflow, resumeWorkflowRun, getBrickResources, doneBrick } from '../../redux';
import { Utils } from '../../services';
import { shouldSkipGenericCompletion } from '../../services/workflowCompletionUtils';
import {
    findMessageShowBrickForEndScreen,
    resolveMessageShowHtml,
    workflowHasMessageShowEnd,
} from '../../services/workflowMessageShowUtils';
import { useEmbeddedResize } from '../../hooks/useEmbeddedResize';
import WorkflowCompletion from './WorkflowCompletion';
import MessageShowEndScreen from './MessageShowEndScreen';

import './WorkflowRunner.css';

const WorkflowRunner = ({
    workflow_id,
    tkt_instance_id,
    default_context,
    show_system_messages = true,
    resume_token = null,
    resume_run_id = null,
    resume_event = null,
    resume_cart_id = null,
}) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();

    const { isEmbedded }                                = useSelector(state => state.ui);
    const { workflow, isLoading, errorMessage }         = useSelector(state => state.workflows);

    const containerRef = useRef();
    const messageShowSnapshotRef = useRef(null);
    const hasBootstrappedRunRef = useRef(false);

    const [done, setDone] = useState({});

    useEmbeddedResize(containerRef, [workflow, isLoading]);

    useEffect(() => {
        if (!tkt_instance_id || hasBootstrappedRunRef.current)
            return;

        hasBootstrappedRunRef.current = true;

        if (resume_token || resume_run_id) {
            dispatch(resumeWorkflowRun({
                run_id: resume_run_id,
                event: resume_event,
                cart_id: resume_cart_id,
                resume_token: resume_token,
            }));
            return;
        }

        if (workflow_id) {
            dispatch(startWorkflow(workflow_id, tkt_instance_id, default_context));
        }
    }, [
        workflow_id,
        tkt_instance_id,
        dispatch,
        default_context,
        resume_token,
        resume_run_id,
        resume_event,
        resume_cart_id,
    ]);

    const brickIndex = workflow?.getCurrentBrickIndex();
    const brick = workflow?.getCurrentBrick();
    const lang = Utils.i18nUiLang(i18n);

    useEffect(() => {
        if (brick?.type !== 'message.show')
            return;

        const context = workflow?.context || {};
        const html = resolveMessageShowHtml(brick, context, lang);
        if (html)
            messageShowSnapshotRef.current = { mainHtml: html };
    }, [brick, workflow, lang]);

    const onDone = (context, callback) => {
        setDone({
            ...done,
            [brickIndex]: true
        });

        if (done[brickIndex])
            return;

        dispatch(doneBrick(
            workflow.id,
            tkt_instance_id,
            context,
            (err, workflow) => {
                callback && callback(err, workflow);
            }
        ));
    };

    useEffect(() => {
        if (!brick || brick?.resources || brick.type === 'message.show' || brick.type === 'auth.kronos.login')
            return;

        const brick_id = brick.id
            || workflow?.context?.__current_brick_id__
            || null;
        if (!brick_id)
            return;

        dispatch(getBrickResources(
            workflow.id,
            tkt_instance_id,
            brick_id
        ));
    }, [brick, dispatch, workflow?.id, workflow?.context?.__current_brick_id__, tkt_instance_id]);

    if (!workflow) {
        if (isLoading) {
            return (
                <div className="loader-wrapper center light">
                    <Loader color="#454545" />
                </div>
            );
        }
        if (errorMessage) {
            return (
                <div className="alert alert-danger">
                    <span dangerouslySetInnerHTML={{ __html: errorMessage }}></span>
                </div>
            );
        }
        return null;
    }

    const errors = workflow.getErrors();
    const messages = workflow.getMessages();
    const has_message_show_end = workflowHasMessageShowEnd(workflow);
    const snapshot_html = messageShowSnapshotRef.current?.mainHtml || '';
    const keep_message_show_visible =
        brick?.type === 'message.show'
        || Boolean(snapshot_html)
        || (workflow.succeeded() && has_message_show_end);

    const renderMessageShowEndScreen = () => (
        <MessageShowEndScreen
            workflow={workflow}
            containerRef={containerRef}
            fallbackMainHtml={snapshot_html}
        />
    );

    if (workflow.succeeded()) {
        if (has_message_show_end || snapshot_html)
            return renderMessageShowEndScreen();
        if (shouldSkipGenericCompletion(workflow))
            return null;
        return <WorkflowCompletion workflow={workflow} containerRef={containerRef} />;
    }

    if (isLoading && !keep_message_show_visible)
        return (
            <div className="loader-wrapper center light">
                <Loader color="#454545" />
            </div>
        );

    if (
        isLoading
        && keep_message_show_visible
        && brick?.type !== 'message.show'
        && (snapshot_html || findMessageShowBrickForEndScreen(workflow))
    )
        return renderMessageShowEndScreen();

    if (workflow.failed())
        return (
            <div className="alert alert-danger">
                {errors.map((error, index) => (
                    <span key={index} dangerouslySetInnerHTML={{__html: error}}></span>
                ))}
            </div>
        );

    if (workflow.isWaiting()) {
        const waiting_ev = workflow.context?.__waiting_event__;
        if (waiting_ev === 'approval.pending') {
            const raw_waiting_message = workflow.context?.__waiting_message__;
            const waiting_html = Utils.localized_or_fallback(raw_waiting_message, lang, '');
            const resolved_waiting_html = waiting_html
                ? Utils.replaceShortcodes(waiting_html, { ...(workflow.context || {}), lang })
                : `<p>${t('workflows.waiting_for_approval')}</p>`;
            return (
                <div className="WorkflowRunner WorkflowRunner--completed" ref={containerRef}>
                    <WorkflowMessagePanel mainHtml={resolved_waiting_html} />
                </div>
            );
        }
        const msg_key = waiting_ev === 'workflow.timer'
            ? 'workflows.waiting_for_timer'
            : 'workflows.waiting_for_payment_confirmation';
        return (
            <div className="alert alert-info">
                {t(msg_key)}
            </div>
        );
    }

    if (!workflow?.bricks?.length)
        return null;

    if ((!brick || brick.server_side) && (snapshot_html || has_message_show_end))
        return renderMessageShowEndScreen();

    if (!brick || brick.server_side)
        return null;

    return (
        <div className="WorkflowRunner" ref={containerRef}>
            {errorMessage && (
                <div className="alert alert-danger">
                    <span dangerouslySetInnerHTML={{__html: errorMessage}}></span>
                </div>
            )}
            {show_system_messages && brick?.type !== 'message.show' && messages?.map((message, index) => (
                <div key={index} className="bg-success p-3 text-white">
                    <span dangerouslySetInnerHTML={{__html: message}}></span>
                </div>
            ))}
            { brick.run(workflow?.context || {}, isEmbedded, onDone) }
        </div>
    );
};

export default WorkflowRunner;
